import posthog from 'posthog-js';
import { sanitizeAnalyticsUrl } from '@/utils/analyticsUrl';
import { readCampaignParams } from '@/utils/campaignAttribution';

/**
 * PostHog product analytics — autocapture + session replay + custom events.
 *
 * Production-gated to match Sentry. Only initialized once; safe to call
 * `initPostHog` more than once (e.g. on hot-reload) — the SDK no-ops on
 * subsequent inits.
 *
 * Person profiles use `identified_only` so anonymous browsing doesn't mint
 * person rows (saves quota). Once `identifyPostHogUser` runs on login, the
 * session is upgraded to an identified profile and prior anonymous events
 * are stitched to that user.
 */

let initialized = false;

export const initPostHog = (): void => {
  if (initialized) return;
  if (import.meta.env.VITE_APP_ENV !== 'production') return;

  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
  const host =
    (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) ||
    'https://us.i.posthog.com';

  if (!key) return;

  posthog.init(key, {
    api_host: host,
    // Autocapture clicks, form submits, page leaves — covers most of the
    // 'feature usage' question without per-component instrumentation.
    autocapture: true,
    // We drive pageviews from the React Router listener in App.tsx so
    // SPA route changes get tracked. Disable PostHog's default which only
    // fires on full document loads.
    capture_pageview: false,
    capture_pageleave: true,
    // Session replay — invaluable for "how is this feature actually used?"
    // and for reproducing bugs reported by users.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        email: false,
      },
    },
    // Don't create a person row until we identify them on login.
    person_profiles: 'identified_only',
    // Best-effort cross-domain tracking (we have dev.espotz.live, espotz.live)
    cross_subdomain_cookie: true,
    persistence: 'localStorage+cookie',
    // Reduce noise in dev console
    loaded: ph => {
      if (import.meta.env.DEV) ph.debug(false);
    },
  });

  // Tag every event with platform context so web vs mobile can be filtered
  // in PostHog dashboards. The mobile app registers `platform: 'mobile'`
  // (see espotz-mobile-app/src/shared/analytics/posthog.ts); both apps share
  // this same PostHog project, so this property must be set symmetrically or
  // any `platform`-based insight would silently drop web events.
  posthog.register({ platform: 'web' });

  initialized = true;
};

export const identifyPostHogUser = (user: {
  id?: string;
  email?: string;
  username?: string;
  countryCode?: string;
}): void => {
  if (!initialized || !user?.id) return;
  posthog.identify(user.id, {
    email: user.email,
    username: user.username,
    country: user.countryCode,
  });
};

export const resetPostHogUser = (): void => {
  if (!initialized) return;
  posthog.reset();
};

/**
 * Capture a custom event. Used by the AnalyticsTracker bridge so existing
 * trackEvent / trackFeatureUsage call sites light up automatically.
 */
export const capturePostHogEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
): void => {
  if (!initialized) return;
  posthog.capture(eventName, properties);
};

const CAMPAIGN_RUNS_SEEN_KEY = 'espotz_campaign_runs_seen';
// Fallback when sessionStorage is blocked: per page load instead of per session.
const campaignRunsSeenInMemory = new Set<string>();

/** True the first time this session lands from a given campaign run (or campaign, if no run). */
const markCampaignLandingSeen = (key: string): boolean => {
  try {
    const seen: string[] = JSON.parse(
      sessionStorage.getItem(CAMPAIGN_RUNS_SEEN_KEY) || '[]',
    );
    if (seen.includes(key)) return false;
    sessionStorage.setItem(
      CAMPAIGN_RUNS_SEEN_KEY,
      JSON.stringify([...seen, key].slice(-20)),
    );
    return true;
  } catch {
    if (campaignRunsSeenInMemory.has(key)) return false;
    campaignRunsSeenInMemory.add(key);
    return true;
  }
};

/**
 * A landing from a campaign email carries its UTM tags only on this one URL; the next route
 * change drops them. Registering them for the session puts `campaign_id` on every later event in
 * this visit (register_succeeded included), and `campaign_link_opened` is the first step of the
 * "Campaign → registration" funnel. Session scope, so next week's visit is not credited.
 */
const captureCampaignLanding = (href: string, path: string): void => {
  const params = readCampaignParams(href);
  if (!params) return;

  posthog.register_for_session({ ...params });
  if (!markCampaignLandingSeen(params.campaign_run_id ?? params.campaign_id))
    return;
  posthog.capture('campaign_link_opened', {
    ...params,
    path: path.split('?')[0],
  });
};

/**
 * Record a pageview. Wired to React Router's location changes in App.tsx.
 */
export const capturePostHogPageview = (path: string, title?: string): void => {
  if (!initialized) return;
  const href = typeof window !== 'undefined' ? window.location.href : path;
  captureCampaignLanding(href, path);
  // Both fields carry the query string, so both are sanitised — a token here would land in
  // analytics and session replay, where it stays valid forever.
  posthog.capture('$pageview', {
    $current_url: sanitizeAnalyticsUrl(href),
    path: sanitizeAnalyticsUrl(path),
    title,
  });
};

/**
 * Set a property on the current person profile (e.g. plan tier, role).
 */
export const setPostHogPersonProperties = (
  properties: Record<string, unknown>,
): void => {
  if (!initialized) return;
  posthog.setPersonProperties(properties);
};
